import { create } from 'zustand';
import axios from 'axios';
import { saveBlobResponse } from '../lib/download';
import { useAuthStore } from './authStore';
import { API_URL } from '../lib/api';

const API = API_URL;

// Cache configuration - how long data stays fresh (in milliseconds)
const CACHE_DURATION = {
  jobs: 30000,        // 30 seconds - jobs may change frequently
  equipment: 60000,   // 1 minute - equipment changes less often
  clients: 60000,     // 1 minute
  crewMembers: 60000, // 1 minute
  settings: 300000,   // 5 minutes - settings rarely change
  dashboardStats: 30000, // 30 seconds
  daySheets: 30000,   // 30 seconds
};

const getAuthHeader = () => {
  const token = useAuthStore.getState().sessionToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper to check if cache is still valid
const isCacheValid = (lastFetched, cacheKey) => {
  if (!lastFetched) return false;
  const duration = CACHE_DURATION[cacheKey] || 30000;
  return Date.now() - lastFetched < duration;
};

export const useAppStore = create((set, get) => ({
  // Jobs
  jobs: [],
  currentJob: null,
  jobsLoading: false,
  jobsLastFetched: null,
  
  // Equipment
  equipment: [],
  equipmentLoading: false,
  equipmentLastFetched: null,
  
  // Clients
  clients: [],
  clientsLoading: false,
  clientsLastFetched: null,

  briefs: [],
  briefsLoading: false,
  treatments: [],
  treatmentsLoading: false,
  
  // Crew Members
  crewMembers: [],
  crewMembersLoading: false,
  crewMembersLastFetched: null,
  
  // Dashboard
  dashboardStats: null,
  statsLoading: false,
  statsLastFetched: null,
  
  // Day Sheets
  daySheets: [],
  daysheetsLoading: false,
  daySheetJobId: null,
  daysheetsLastFetched: null,
  
  // Settings
  settings: null,
  settingsLoading: false,
  settingsLastFetched: null,

  // ============ CACHE HELPERS ============
  invalidateCache: (cacheKey) => {
    if (cacheKey === 'all') {
      set({
        jobsLastFetched: null,
        equipmentLastFetched: null,
        clientsLastFetched: null,
        crewMembersLastFetched: null,
        settingsLastFetched: null,
        statsLastFetched: null,
        daysheetsLastFetched: null,
      });
    } else {
      set({ [`${cacheKey}LastFetched`]: null });
    }
  },

  // Clear ALL data - MUST be called on logout and before login to prevent data leakage between users
  clearAllData: () => {
    // Remove user_region from localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('user_region');
    }
    
    set({
      // Jobs
      jobs: [],
      currentJob: null,
      jobsLoading: false,
      jobsLastFetched: null,
      
      // Equipment
      equipment: [],
      equipmentLoading: false,
      equipmentLastFetched: null,
      
      // Clients
      clients: [],
      clientsLoading: false,
      clientsLastFetched: null,
      
      // Crew Members
      crewMembers: [],
      crewMembersLoading: false,
      crewMembersLastFetched: null,
      
      // Dashboard
      dashboardStats: null,
      statsLoading: false,
      statsLastFetched: null,
      
      // Day Sheets
      daySheets: [],
      daysheetsLoading: false,
      daySheetJobId: null,
      daysheetsLastFetched: null,
      
      // Settings
      settings: null,
      settingsLoading: false,
      settingsLastFetched: null,
      
      // Equipment Kits
      equipmentKits: [],
      kitsLoading: false,
      
      // ROI Projection
      roiProjection: null,
      roiProjectionLoading: false,
    });
  },

  // ============ CLIENT ACTIONS ============
  fetchClients: async (forceRefresh = false) => {
    // Skip fetch if data is cached and still fresh
    if (!forceRefresh && get().clients.length > 0 && isCacheValid(get().clientsLastFetched, 'clients')) {
      return get().clients;
    }
    
    set({ clientsLoading: true });
    try {
      const response = await axios.get(`${API}/clients`, { headers: getAuthHeader() });
      set({ clients: response.data, clientsLoading: false, clientsLastFetched: Date.now() });
      return response.data;
    } catch (error) {
      console.error('Error fetching clients:', error);
      set({ clientsLoading: false });
      return [];
    }
  },

  createClient: async (clientData) => {
    try {
      const response = await axios.post(`${API}/clients`, clientData, { headers: getAuthHeader() });
      set({ clients: [...get().clients, response.data] });
      return response.data;
    } catch (error) {
      console.error('Error creating client:', error);
      throw error;
    }
  },

  updateClient: async (clientId, clientData) => {
    try {
      const response = await axios.put(`${API}/clients/${clientId}`, clientData, { headers: getAuthHeader() });
      set({ clients: get().clients.map(c => c.id === clientId ? response.data : c) });
      return response.data;
    } catch (error) {
      console.error('Error updating client:', error);
      throw error;
    }
  },

  deleteClient: async (clientId) => {
    try {
      await axios.delete(`${API}/clients/${clientId}`, { headers: getAuthHeader() });
      set({ clients: get().clients.filter(c => c.id !== clientId) });
    } catch (error) {
      console.error('Error deleting client:', error);
      throw error;
    }
  },

  // ============ BRIEF ACTIONS ============
  fetchBriefs: async () => {
    set({ briefsLoading: true });
    try {
      const response = await axios.get(`${API}/briefs`, { headers: getAuthHeader() });
      set({ briefs: response.data, briefsLoading: false });
      return response.data;
    } catch (error) {
      console.error('Error fetching briefs:', error);
      set({ briefsLoading: false });
      return [];
    }
  },

  getBrief: async (briefId) => {
    const response = await axios.get(`${API}/briefs/${briefId}`, { headers: getAuthHeader() });
    return response.data;
  },

  createBrief: async (briefData) => {
    const response = await axios.post(`${API}/briefs`, briefData, { headers: getAuthHeader() });
    set({ briefs: [response.data, ...get().briefs] });
    return response.data;
  },

  updateBrief: async (briefId, briefData) => {
    const response = await axios.put(`${API}/briefs/${briefId}`, briefData, { headers: getAuthHeader() });
    set({ briefs: get().briefs.map(b => b.id === briefId ? response.data : b) });
    return response.data;
  },

  deleteBrief: async (briefId) => {
    await axios.delete(`${API}/briefs/${briefId}`, { headers: getAuthHeader() });
    set({ briefs: get().briefs.filter(b => b.id !== briefId) });
  },

  applyBriefResponse: async (briefId) => {
    const response = await axios.post(`${API}/briefs/${briefId}/apply-response`, {}, { headers: getAuthHeader() });
    set({ briefs: get().briefs.map(b => b.id === briefId ? response.data : b) });
    return response.data;
  },

  // ============ TREATMENT ACTIONS ============
  fetchTreatments: async () => {
    set({ treatmentsLoading: true });
    try {
      const response = await axios.get(`${API}/treatments`, { headers: getAuthHeader() });
      set({ treatments: response.data, treatmentsLoading: false });
      return response.data;
    } catch (error) {
      console.error('Error fetching treatments:', error);
      set({ treatmentsLoading: false });
      return [];
    }
  },

  /** Treatments linked to a job, newest first. Doesn't touch the treatments list in the store. */
  getTreatmentsForJob: async (jobId) => {
    const response = await axios.get(`${API}/treatments`, { params: { job_id: jobId }, headers: getAuthHeader() });
    return response.data;
  },

  getTreatment: async (treatmentId) => {
    const response = await axios.get(`${API}/treatments/${treatmentId}`, { headers: getAuthHeader() });
    return response.data;
  },

  createTreatment: async (treatmentData) => {
    const response = await axios.post(`${API}/treatments`, treatmentData, { headers: getAuthHeader() });
    set({ treatments: [response.data, ...get().treatments] });
    return response.data;
  },

  updateTreatment: async (treatmentId, treatmentData) => {
    const response = await axios.put(`${API}/treatments/${treatmentId}`, treatmentData, { headers: getAuthHeader() });
    set({ treatments: get().treatments.map(t => t.id === treatmentId ? response.data : t) });
    return response.data;
  },

  getTreatmentSourcing: async (treatmentId) => {
    const response = await axios.get(`${API}/treatments/${treatmentId}/sourcing`, { headers: getAuthHeader() });
    return response.data;
  },

  pushTreatmentToJob: async (treatmentId, options) => {
    const response = await axios.post(`${API}/treatments/${treatmentId}/push-to-job`, options, { headers: getAuthHeader() });
    return response.data;
  },

  /** body: { include_equipment, images: { referenceId: dataUrl } } - see lib/treatmentPdf.js */
  downloadTreatmentPdf: async (treatmentId, body) => {
    const response = await axios.post(`${API}/treatments/${treatmentId}/pdf`, body, {
      headers: getAuthHeader(),
      responseType: 'blob',
    });
    saveBlobResponse(response, 'Treatment.pdf', 'application/pdf');
  },

  resetTreatmentShareLink: async (treatmentId) => {
    const response = await axios.post(`${API}/treatments/${treatmentId}/share-link/reset`, {}, { headers: getAuthHeader() });
    set({ treatments: get().treatments.map(t => t.id === treatmentId ? response.data : t) });
    return response.data;
  },

  // The link-preview image: which frame it was made from, and storing a new one
  getTreatmentShareImageSource: async (treatmentId) => {
    const response = await axios.get(`${API}/treatments/${treatmentId}/share-image`, { headers: getAuthHeader() });
    return response.data.source_url;
  },

  putTreatmentShareImage: async (treatmentId, image, sourceUrl) => {
    await axios.put(`${API}/treatments/${treatmentId}/share-image`, { image, source_url: sourceUrl }, { headers: getAuthHeader() });
  },

  deleteTreatment: async (treatmentId) => {
    await axios.delete(`${API}/treatments/${treatmentId}`, { headers: getAuthHeader() });
    set({ treatments: get().treatments.filter(t => t.id !== treatmentId) });
  },

  // ============ CREW MEMBER ACTIONS ============
  fetchCrewMembers: async (forceRefresh = false) => {
    // Skip fetch if data is cached and still fresh
    if (!forceRefresh && get().crewMembers.length > 0 && isCacheValid(get().crewMembersLastFetched, 'crewMembers')) {
      return get().crewMembers;
    }
    
    set({ crewMembersLoading: true });
    try {
      const response = await axios.get(`${API}/crew`, { headers: getAuthHeader() });
      set({ crewMembers: response.data, crewMembersLoading: false, crewMembersLastFetched: Date.now() });
      return response.data;
    } catch (error) {
      console.error('Error fetching crew members:', error);
      set({ crewMembersLoading: false });
      return [];
    }
  },

  createCrewMember: async (crewData) => {
    try {
      const response = await axios.post(`${API}/crew`, crewData, { headers: getAuthHeader() });
      set({ crewMembers: [...get().crewMembers, response.data] });
      return response.data;
    } catch (error) {
      console.error('Error creating crew member:', error);
      throw error;
    }
  },

  updateCrewMember: async (crewId, crewData) => {
    try {
      const response = await axios.put(`${API}/crew/${crewId}`, crewData, { headers: getAuthHeader() });
      set({ crewMembers: get().crewMembers.map(c => c.id === crewId ? response.data : c) });
      return response.data;
    } catch (error) {
      console.error('Error updating crew member:', error);
      throw error;
    }
  },

  deleteCrewMember: async (crewId) => {
    try {
      await axios.delete(`${API}/crew/${crewId}`, { headers: getAuthHeader() });
      // Update the crew member's status to archived instead of removing
      set({ crewMembers: get().crewMembers.map(c => c.id === crewId ? { ...c, status: 'archived' } : c) });
    } catch (error) {
      console.error('Error deleting crew member:', error);
      throw error;
    }
  },

  // ============ SETTINGS ACTIONS ============
  fetchSettings: async (forceRefresh = false) => {
    // Skip fetch if data is cached and still fresh
    if (!forceRefresh && get().settings && isCacheValid(get().settingsLastFetched, 'settings')) {
      return get().settings;
    }
    
    set({ settingsLoading: true });
    try {
      const response = await axios.get(`${API}/settings`, { headers: getAuthHeader() });
      set({ settings: response.data, settingsLoading: false, settingsLastFetched: Date.now() });
      // Cache region in localStorage for instant availability
      if (response.data?.region && typeof window !== 'undefined') {
        localStorage.setItem('user_region', response.data.region);
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      set({ settingsLoading: false });
      return null;
    }
  },

  updateSettings: async (settingsData) => {
    try {
      const response = await axios.put(`${API}/settings`, settingsData, { headers: getAuthHeader() });
      set({ settings: response.data, settingsLastFetched: Date.now() });
      // Cache region in localStorage
      if (response.data?.region && typeof window !== 'undefined') {
        localStorage.setItem('user_region', response.data.region);
      }
      return response.data;
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  },

  // Job Numbering Actions
  getJobNumberPreview: async () => {
    try {
      const response = await axios.get(`${API}/settings/job-number-preview`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error getting job number preview:', error);
      return null;
    }
  },

  renumberJobs: async () => {
    try {
      const response = await axios.post(`${API}/settings/renumber-jobs`, {}, { headers: getAuthHeader() });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error renumbering jobs:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to renumber jobs' };
    }
  },

  // Jobs Actions
  fetchJobs: async (forceRefresh = false) => {
    // Skip fetch if data is cached and still fresh. Only a real fetch sets
    // jobsLastFetched: a create or duplicate on a list that was never loaded
    // leaves one job in it, and stamping that fresh hid every other job.
    if (!forceRefresh && get().jobs.length > 0 && isCacheValid(get().jobsLastFetched, 'jobs')) {
      return get().jobs;
    }
    
    set({ jobsLoading: true });
    try {
      const response = await axios.get(`${API}/jobs`, { headers: getAuthHeader() });
      set({ jobs: response.data, jobsLoading: false, jobsLastFetched: Date.now() });
      return response.data;
    } catch (error) {
      console.error('Error fetching jobs:', error);
      set({ jobsLoading: false });
      return [];
    }
  },

  fetchJob: async (jobId) => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}`, { headers: getAuthHeader() });
      set({ currentJob: response.data });
      return response.data;
    } catch (error) {
      console.error('Error fetching job:', error);
      return null;
    }
  },

  // Update a single job in the jobs array without refetching all
  updateJobInList: async (jobId) => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}`, { headers: getAuthHeader() });
      set((state) => ({
        jobs: state.jobs.map(job => job.id === jobId ? response.data : job),
      }));
      return response.data;
    } catch (error) {
      console.error('Error updating job:', error);
      return null;
    }
  },

  createJob: async (jobData) => {
    try {
      const response = await axios.post(`${API}/jobs`, jobData, { headers: getAuthHeader() });
      set((state) => ({ jobs: [...state.jobs, response.data] }));
      return response.data;
    } catch (error) {
      console.error('Error creating job:', error);
      throw error;
    }
  },

  updateJob: async (jobId, jobData) => {
    try {
      const response = await axios.put(`${API}/jobs/${jobId}`, jobData, { headers: getAuthHeader() });
      set((state) => ({
        jobs: state.jobs.map(j => j.id === jobId ? response.data : j),
        currentJob: response.data,
      }));
      return response.data;
    } catch (error) {
      console.error('Error updating job:', error);
      throw error;
    }
  },

  deleteJob: async (jobId) => {
    try {
      await axios.delete(`${API}/jobs/${jobId}`, { headers: getAuthHeader() });
      set((state) => ({ jobs: state.jobs.filter(j => j.id !== jobId) }));
    } catch (error) {
      console.error('Error deleting job:', error);
      throw error;
    }
  },

  duplicateJob: async (jobId) => {
    try {
      const response = await axios.post(`${API}/jobs/${jobId}/duplicate`, {}, { headers: getAuthHeader() });
      set((state) => ({ jobs: [...state.jobs, response.data] }));
      return response.data;
    } catch (error) {
      console.error('Error duplicating job:', error);
      throw error;
    }
  },

  // Equipment Actions
  fetchEquipment: async (forceRefresh = false) => {
    // Skip fetch if data is cached and still fresh
    if (!forceRefresh && get().equipment.length > 0 && isCacheValid(get().equipmentLastFetched, 'equipment')) {
      return get().equipment;
    }
    
    set({ equipmentLoading: true });
    try {
      const response = await axios.get(`${API}/equipment`, { headers: getAuthHeader() });
      set({ equipment: response.data, equipmentLoading: false, equipmentLastFetched: Date.now() });
      return response.data;
    } catch (error) {
      console.error('Error fetching equipment:', error);
      set({ equipmentLoading: false });
      return [];
    }
  },

  createEquipment: async (equipData) => {
    try {
      const response = await axios.post(`${API}/equipment`, equipData, { headers: getAuthHeader() });
      set((state) => ({ equipment: [...state.equipment, response.data], equipmentLastFetched: Date.now() }));
      return response.data;
    } catch (error) {
      console.error('Error creating equipment:', error);
      throw error;
    }
  },

  updateEquipment: async (equipId, equipData) => {
    try {
      const response = await axios.put(`${API}/equipment/${equipId}`, equipData, { headers: getAuthHeader() });
      set((state) => ({
        equipment: state.equipment.map(e => e.id === equipId ? response.data : e),
        equipmentLastFetched: Date.now()
      }));
      return response.data;
    } catch (error) {
      console.error('Error updating equipment:', error);
      throw error;
    }
  },

  deleteEquipment: async (equipId, disposalData = null) => {
    try {
      const config = { 
        headers: getAuthHeader(),
        data: disposalData ? { disposal: disposalData } : undefined
      };
      await axios.delete(`${API}/equipment/${equipId}`, config);
      
      // Mark as archived in local state with disposal data
      set((state) => ({ 
        equipment: state.equipment.map(e => 
          e.id === equipId ? { 
            ...e, 
            archived: true, 
            is_active: false,
            ...(disposalData || {})
          } : e
        ),
        equipmentLastFetched: Date.now()
      }));
    } catch (error) {
      console.error('Error archiving equipment:', error);
      throw error;
    }
  },

  restoreEquipment: async (equipId) => {
    try {
      const response = await axios.post(`${API}/equipment/${equipId}/restore`, {}, { headers: getAuthHeader() });
      set((state) => ({
        equipment: state.equipment.map(e => e.id === equipId ? response.data : e),
        equipmentLastFetched: Date.now()
      }));
      return response.data;
    } catch (error) {
      console.error('Error restoring equipment:', error);
      throw error;
    }
  },

  permanentDeleteEquipment: async (equipId) => {
    try {
      await axios.delete(`${API}/equipment/${equipId}/permanent`, { headers: getAuthHeader() });
      set((state) => ({
        equipment: state.equipment.filter(e => e.id !== equipId),
        equipmentLastFetched: Date.now()
      }));
    } catch (error) {
      console.error('Error permanently deleting equipment:', error);
      throw error;
    }
  },

  fetchEquipmentById: async (equipId) => {
    try {
      const response = await axios.get(`${API}/equipment/${equipId}`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error fetching equipment:', error);
      throw error;
    }
  },

  addHistoricalIncome: async (equipId, entry) => {
    try {
      const response = await axios.post(`${API}/equipment/${equipId}/historical-income`, entry, { headers: getAuthHeader() });
      set((state) => ({
        equipment: state.equipment.map(e => e.id === equipId ? response.data : e)
      }));
      return response.data;
    } catch (error) {
      console.error('Error adding historical income:', error);
      throw error;
    }
  },

  removeHistoricalIncome: async (equipId, entryId) => {
    try {
      const response = await axios.delete(`${API}/equipment/${equipId}/historical-income/${entryId}`, { headers: getAuthHeader() });
      set((state) => ({
        equipment: state.equipment.map(e => e.id === equipId ? response.data : e)
      }));
      return response.data;
    } catch (error) {
      console.error('Error removing historical income:', error);
      throw error;
    }
  },

  updateHistoricalIncome: async (equipId, entryId, entry) => {
    try {
      const response = await axios.put(`${API}/equipment/${equipId}/historical-income/${entryId}`, entry, { headers: getAuthHeader() });
      set((state) => ({
        equipment: state.equipment.map(e => e.id === equipId ? response.data : e)
      }));
      return response.data;
    } catch (error) {
      console.error('Error updating historical income:', error);
      throw error;
    }
  },

  bulkImportHistoricalIncome: async (equipId, entries) => {
    try {
      const response = await axios.post(`${API}/equipment/${equipId}/historical-income/bulk`, { entries }, { headers: getAuthHeader() });
      set((state) => ({
        equipment: state.equipment.map(e => e.id === equipId ? response.data.equipment : e)
      }));
      return response.data;
    } catch (error) {
      console.error('Error bulk importing historical income:', error);
      throw error;
    }
  },

  recalculateEquipmentROI: async () => {
    try {
      const response = await axios.post(`${API}/equipment/recalculate-roi`, {}, { headers: getAuthHeader() });
      // Refresh equipment list
      const equipResponse = await axios.get(`${API}/equipment`, { headers: getAuthHeader() });
      set({ equipment: equipResponse.data });
      return response.data;
    } catch (error) {
      console.error('Error recalculating ROI:', error);
      throw error;
    }
  },

  roiProjection: null,
  roiProjectionLoading: false,

  fetchROIProjection: async () => {
    set({ roiProjectionLoading: true });
    try {
      const response = await axios.get(`${API}/equipment/roi-projection`, { headers: getAuthHeader() });
      set({ roiProjection: response.data, roiProjectionLoading: false });
      return response.data;
    } catch (error) {
      console.error('Error fetching ROI projection:', error);
      set({ roiProjectionLoading: false });
      throw error;
    }
  },

  // ============ EQUIPMENT KIT ACTIONS ============
  equipmentKits: [],
  kitsLoading: false,

  fetchEquipmentKits: async () => {
    set({ kitsLoading: true });
    try {
      const response = await axios.get(`${API}/equipment-kits`, { headers: getAuthHeader() });
      set({ equipmentKits: response.data, kitsLoading: false });
      return response.data;
    } catch (error) {
      console.error('Error fetching equipment kits:', error);
      set({ kitsLoading: false });
      return [];
    }
  },

  createEquipmentKit: async (kitData) => {
    try {
      const response = await axios.post(`${API}/equipment-kits`, kitData, { headers: getAuthHeader() });
      set((state) => ({ equipmentKits: [...state.equipmentKits, response.data] }));
      return response.data;
    } catch (error) {
      console.error('Error creating equipment kit:', error);
      throw error;
    }
  },

  updateEquipmentKit: async (kitId, kitData) => {
    try {
      const response = await axios.put(`${API}/equipment-kits/${kitId}`, kitData, { headers: getAuthHeader() });
      set((state) => ({
        equipmentKits: state.equipmentKits.map(k => k.id === kitId ? response.data : k)
      }));
      return response.data;
    } catch (error) {
      console.error('Error updating equipment kit:', error);
      throw error;
    }
  },

  deleteEquipmentKit: async (kitId) => {
    try {
      await axios.delete(`${API}/equipment-kits/${kitId}`, { headers: getAuthHeader() });
      set((state) => ({ equipmentKits: state.equipmentKits.filter(k => k.id !== kitId) }));
    } catch (error) {
      console.error('Error deleting equipment kit:', error);
      throw error;
    }
  },

  getKitWithDetails: async (kitId) => {
    try {
      const response = await axios.get(`${API}/equipment-kits/${kitId}/expanded`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error fetching kit details:', error);
      throw error;
    }
  },

  // Dashboard Actions
  fetchDashboardStats: async (forceRefresh = false) => {
    // Skip fetch if data is cached and still fresh
    if (!forceRefresh && get().dashboardStats && isCacheValid(get().statsLastFetched, 'dashboardStats')) {
      return get().dashboardStats;
    }
    
    set({ statsLoading: true });
    try {
      const response = await axios.get(`${API}/dashboard/stats`, { headers: getAuthHeader() });
      set({ dashboardStats: response.data, statsLoading: false, statsLastFetched: Date.now() });
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      set({ statsLoading: false });
      return null;
    }
  },

  // Day Sheets Actions
  fetchDaySheets: async (jobId, forceRefresh = false) => {
    // Skip fetch if data is cached for the same job and still fresh
    if (!forceRefresh && get().daySheetJobId === jobId && get().daySheets.length > 0 && isCacheValid(get().daysheetsLastFetched, 'daySheets')) {
      return get().daySheets;
    }
    
    set({ daysheetsLoading: true });
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/daysheets`, { headers: getAuthHeader() });
      set({ daySheets: response.data, daysheetsLoading: false, daySheetJobId: jobId, daysheetsLastFetched: Date.now() });
      return response.data;
    } catch (error) {
      console.error('Error fetching day sheets:', error);
      set({ daysheetsLoading: false });
      return [];
    }
  },

  createDaySheet: async (jobId, sheetData) => {
    try {
      const response = await axios.post(`${API}/jobs/${jobId}/daysheets`, sheetData, { headers: getAuthHeader() });
      set((state) => ({ daySheets: [...state.daySheets, response.data], daysheetsLastFetched: Date.now() }));
      return response.data;
    } catch (error) {
      console.error('Error creating day sheet:', error);
      throw error;
    }
  },

  updateDaySheet: async (sheetId, sheetData) => {
    try {
      const response = await axios.put(`${API}/daysheets/${sheetId}`, sheetData, { headers: getAuthHeader() });
      set((state) => ({
        daySheets: state.daySheets.map(s => s.id === sheetId ? response.data : s)
      }));
      return response.data;
    } catch (error) {
      console.error('Error updating day sheet:', error);
      throw error;
    }
  },

  deleteDaySheet: async (sheetId) => {
    try {
      await axios.delete(`${API}/daysheets/${sheetId}`, { headers: getAuthHeader() });
      set((state) => ({ daySheets: state.daySheets.filter(s => s.id !== sheetId) }));
    } catch (error) {
      console.error('Error deleting day sheet:', error);
      throw error;
    }
  },

  // PDF Downloads
  downloadQuotePDF: async (jobId) => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/quote-pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Extract filename from Content-Disposition header, fallback to default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `estimate_${jobId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading estimate PDF:', error);
      throw error;
    }
  },

  downloadInvoicePDF: async (jobId) => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/invoice-pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Extract filename from Content-Disposition header, fallback to default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `invoice_${jobId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
      throw error;
    }
  },

  downloadXeroCSV: async (jobId) => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/xero-csv`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Extract filename from Content-Disposition header, fallback to default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `xero_invoice_${jobId}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading Xero CSV:', error);
      throw error;
    }
  },

  // Xero Direct Integration
  getXeroStatus: async () => {
    try {
      const response = await axios.get(`${API}/xero/status`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error getting Xero status:', error);
      return { connected: false, tenant_name: null };
    }
  },

  getXeroAuthUrl: async () => {
    try {
      const response = await axios.get(`${API}/xero/auth-url`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error getting Xero auth URL:', error);
      throw error;
    }
  },

  disconnectXero: async () => {
    try {
      const response = await axios.post(`${API}/xero/disconnect`, {}, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error disconnecting Xero:', error);
      throw error;
    }
  },

  getXeroAccounts: async () => {
    try {
      const response = await axios.get(`${API}/xero/accounts`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error getting Xero accounts:', error);
      throw error;
    }
  },

  getXeroTaxRates: async () => {
    try {
      const response = await axios.get(`${API}/xero/tax-rates`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error getting Xero tax rates:', error);
      throw error;
    }
  },

  getXeroAccountMappings: async () => {
    try {
      const response = await axios.get(`${API}/xero/account-mappings`, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error getting Xero account mappings:', error);
      return { labour_account: '200', equipment_account: '200', expenses_account: '200' };
    }
  },

  updateXeroAccountMappings: async (mappings) => {
    try {
      const response = await axios.put(`${API}/xero/account-mappings`, mappings, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error updating Xero account mappings:', error);
      throw error;
    }
  },

  sendJobToXero: async (jobId) => {
    try {
      const response = await axios.post(`${API}/jobs/${jobId}/send-to-xero`, {}, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error sending job to Xero:', error);
      throw error;
    }
  },

  downloadJobEquipmentListPDF: async (jobId) => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/equipment-list-pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Extract filename from Content-Disposition header, fallback to default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Equipment_List_${jobId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading Job Equipment List PDF:', error);
      throw error;
    }
  },

  downloadEquipmentListPDF: async () => {
    try {
      const response = await axios.get(`${API}/equipment/list-pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Extract filename from Content-Disposition header, fallback to default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Equipment_List.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading equipment list PDF:', error);
      throw error;
    }
  },
}));
